import { CampaignInquiryProductInput, Client } from "src/graphql/generated/graphql";
import { DeliveryState } from "src/hooks/campaigns";
import { DeepPartial } from "src/types/utils";

import type { CreateCampaignInquiryFormValues } from "./formContext";

const mapProduct = (product: DeepPartial<CampaignInquiryProductInput>) => ({
    ...product,
    category: {
        key: product.category?.key || "",
    },
    attributes: product.attributes
        ? product.attributes.map((attr) => ({ id: attr.id || "", locale: attr.locale || "", name: attr.name || "" }))
        : [],
    destinations: product.destinations
        ? product.destinations.map((dest) => ({
              // Spread first: enumerating the fields here silently dropped isOrderNumber, so
              // Datart campaigns never asked testers for an order number (TTV2-1623). `config`
              // stays `?? undefined` — the tenant catalog omits it, and "" is not "unset".
              ...dest,
              key: dest.key || "",
              name: dest.name || "",
              type: dest.type || "text",
              config: dest.config ?? undefined,
              configFields: dest.configFields
                  ? dest.configFields.map((field) => ({
                        label: field.label || "",
                        path: field.path || "",
                        type: field.type || "text",
                        placeholder: field.placeholder || "",
                        isRequiredForApproval: field.isRequiredForApproval || false,
                        urlStripQueryString: field.urlStripQueryString || false,
                    }))
                  : [],
          }))
        : [],
});

export type PartialCreateCampaignInquiryInput = DeepPartial<CreateCampaignInquiryFormValues>;

export const getFormValues = (values?: PartialCreateCampaignInquiryInput): PartialCreateCampaignInquiryInput => ({
    name: values?.name ?? "",
    clientId: values?.clientId ?? "",
    tenantId: values?.tenantId ?? null,
    displayTenantIds: values?.displayTenantIds ?? [],
    mainTenantId: values?.mainTenantId ?? null,
    products: values?.products ? values.products.map(mapProduct) : [],
    description: values?.description ?? null,
    settings: {
        capacity: values?.settings?.capacity ?? 0,
        openAt: values?.settings?.openAt ?? new Date().getTime(),
    },
    restrictions: values?.restrictions ?? null,
    pricing: {
        isFree: values?.pricing?.isFree ?? true,
        discountPercent: values?.pricing?.discountPercent ?? 100,
        discountCodes: values?.pricing?.discountCodes ?? [],
    },
    delivery: {
        freeOfCharge: values?.delivery?.freeOfCharge ?? false,
        type: values?.delivery?.type ?? DeliveryState.PersonalPickup,
        notes: values?.delivery?.notes ?? "",
    },
    questionnaire: values?.questionnaire ?? null,
    thumbnailImage: values?.thumbnailImage ?? null,
    images: values?.images ?? [],
    additionalNotes: values?.additionalNotes ?? "",
});

/**
 * A new inquiry always starts from a clean sheet. Restoring a saved draft is a
 * separate, explicit act — see readCampaignInquiryDraft below.
 */
export const getDefaultValues = (client: Client): PartialCreateCampaignInquiryInput => ({
    ...getFormValues(),
    clientId: client?.id,
});

/*
 * Draft persistence (TTV2-1569).
 *
 * A draft is scoped to the client AND to the intent it was written under, and
 * carries its schema version plus the time it was saved. All of that is checked
 * when the draft is READ, so a draft belonging to another intent, to an older
 * form shape, or to a session abandoned days ago simply cannot be claimed. That
 * matters because the alternative — clearing the draft when the form unmounts —
 * cannot carry the guarantee: a reload or a closed tab runs no teardown, and
 * making cleanup responsible for correctness would also throw away the draft in
 * exactly the crash cases it exists for.
 *
 * Reading a draft never applies it. The caller offers it to the client.
 */
export const CAMPAIGN_INQUIRY_DRAFT_VERSION = 2;

const DRAFT_KEY_PREFIX = "createCampaignInquiryForm";
/** Pre-TTV2-1569 drafts lived in a single unscoped, unstamped slot per browser. */
const LEGACY_DRAFT_KEY = DRAFT_KEY_PREFIX;
const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

export type CampaignInquiryDraftIntent = "new" | `copy:${string}`;

interface CampaignInquiryDraft {
    version: number;
    savedAt: number;
    clientId: string;
    intent: CampaignInquiryDraftIntent;
    values: PartialCreateCampaignInquiryInput;
}

const draftKey = (clientId: string, intent: CampaignInquiryDraftIntent) =>
    `${DRAFT_KEY_PREFIX}:${clientId}:${intent}`;

// Safari with cookies/site-data blocked throws on localStorage instead of
// returning null, and TTV2-1569 was reported on Safari — so guard every access.
const readStorage = (key: string) => {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
};

const writeStorage = (key: string, value: string) => {
    try {
        localStorage.setItem(key, value);
    } catch {
        // Storage unavailable. The form still works, it just won't survive a reload.
    }
};

const removeStorage = (key: string) => {
    try {
        localStorage.removeItem(key);
    } catch {
        // Nothing to do.
    }
};

export const clearCampaignInquiryDraft = (clientId: string, intent: CampaignInquiryDraftIntent) => {
    if (clientId) {
        removeStorage(draftKey(clientId, intent));
    }
    removeStorage(LEGACY_DRAFT_KEY);
};

export const writeCampaignInquiryDraft = (
    clientId: string,
    intent: CampaignInquiryDraftIntent,
    values: PartialCreateCampaignInquiryInput
) => {
    if (!clientId) {
        return;
    }

    const draft: CampaignInquiryDraft = {
        version: CAMPAIGN_INQUIRY_DRAFT_VERSION,
        savedAt: new Date().getTime(),
        clientId,
        intent,
        values,
    };

    writeStorage(draftKey(clientId, intent), JSON.stringify(draft));
};

export const readCampaignInquiryDraft = (
    clientId: string,
    intent: CampaignInquiryDraftIntent
): PartialCreateCampaignInquiryInput | null => {
    // A draft written before TTV2-1569 is unscoped and unstamped — it is exactly
    // the state that used to reappear behind "create a new inquiry" — so drop it
    // rather than trying to interpret it.
    removeStorage(LEGACY_DRAFT_KEY);

    if (!clientId) {
        return null;
    }

    const key = draftKey(clientId, intent);
    const raw = readStorage(key);

    if (!raw) {
        return null;
    }

    let draft: CampaignInquiryDraft;
    try {
        draft = JSON.parse(raw);
    } catch {
        removeStorage(key);
        return null;
    }

    const isClaimable =
        draft?.version === CAMPAIGN_INQUIRY_DRAFT_VERSION &&
        draft?.clientId === clientId &&
        draft?.intent === intent &&
        typeof draft?.savedAt === "number" &&
        new Date().getTime() - draft.savedAt < DRAFT_TTL_MS &&
        !!draft?.values;

    if (!isClaimable) {
        removeStorage(key);
        return null;
    }

    return draft.values;
};
